import { useUnit } from 'effector-react';
import { Link2Off, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { $project } from '../../entities/project';
import { routes } from '../../shared/routing';
import { Button } from '../../shared/ui';
import { ProjectWorkspace } from '../project';
import { $sharedStatus } from './model';
import './model';

/** A read-only share link: the project's latest saved version, or why it can't be shown. */
export function SharedPage() {
  const { t } = useTranslation();
  const [status, project] = useUnit([$sharedStatus, $project]);

  useEffect(() => {
    if (status !== 'ready' || !project) return;
    const previous = document.title;
    document.title = t('viewer.documentTitle', { name: project.name });
    return () => {
      document.title = previous;
    };
  }, [status, project, t]);

  if (status === 'ready' && project) return <ProjectWorkspace />;
  if (status === 'loading' || status === 'ready') {
    return (
      <main className="flex h-full items-center justify-center bg-bg-0">
        <p role="status" className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t('viewer.loading')}
        </p>
      </main>
    );
  }
  return <SharedProblem status={status} />;
}

function SharedProblem({ status }: { status: 'notFound' | 'outdated' | 'error' }) {
  const { t } = useTranslation();
  const goHome = useUnit(routes.home.open);
  return (
    <main className="flex h-full items-center justify-center bg-bg-0 p-4">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-border-subtle bg-bg-1 p-6 text-center shadow-popover">
        <Link2Off className="mx-auto size-6 text-text-3" aria-hidden />
        <h1 className="text-lg font-semibold">{t(`viewer.problems.${status}.title`)}</h1>
        <p role="alert" className="text-sm text-text-2">
          {t(`viewer.problems.${status}.body`)}
        </p>
        <div className="flex justify-center gap-2">
          {status !== 'notFound' && (
            <Button
              variant="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              {t('viewer.reload')}
            </Button>
          )}
          <Button
            variant={status === 'notFound' ? 'primary' : 'ghost'}
            onClick={() => {
              void goHome();
            }}
          >
            {t('viewer.openKasora')}
          </Button>
        </div>
      </div>
    </main>
  );
}
